// pages/api-keys-page.tsx

import React, { useCallback, useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Link } from "react-router-dom";
import { PageLayout } from "../components/page-layout";
import { ApiKey } from "../models/api-key";
import {
  createApiKey,
  deleteApiKey,
  getApiKeys,
} from "../services/api-keys.service";

export const ApiKeysPage: React.FC = () => {
  const { getAccessTokenSilently, isLoading } = useAuth0();
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loadingKeys, setLoadingKeys] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [newKeyName, setNewKeyName] = useState<string>("");
  const [creating, setCreating] = useState<boolean>(false);
  // The full key of the most recently created key. Shown exactly once.
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [createdKeyName, setCreatedKeyName] = useState<string | null>(null);
  const [copied, setCopied] = useState<boolean>(false);

  const [deletingId, setDeletingId] = useState<string | null>(null);

  const refreshKeys = useCallback(async () => {
    try {
      const accessToken = await getAccessTokenSilently();
      const response = await getApiKeys(accessToken);
      if (response.error) {
        setError(response.error.message);
      } else {
        setApiKeys(response.apiKeys || []);
        setError(null);
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoadingKeys(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    refreshKeys();
  }, [refreshKeys]);

  const handleCreate = async () => {
    const name = newKeyName.trim();
    if (!name || creating) {
      return;
    }
    setCreating(true);
    setError(null);
    try {
      const accessToken = await getAccessTokenSilently();
      const response = await createApiKey(accessToken, name);
      if (response.error) {
        setError(response.error.message);
      } else {
        setCreatedKey(response.key);
        setCreatedKeyName(response.apiKey ? response.apiKey.name : name);
        setCopied(false);
        setNewKeyName("");
        await refreshKeys();
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (key: ApiKey) => {
    if (
      !window.confirm(
        `Delete the API key "${key.name}"? Programs using this key will immediately lose access.`
      )
    ) {
      return;
    }
    setDeletingId(key.id);
    setError(null);
    try {
      const accessToken = await getAccessTokenSilently();
      const response = await deleteApiKey(accessToken, key.id);
      if (response.error) {
        setError(response.error.message);
      } else {
        await refreshKeys();
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setDeletingId(null);
    }
  };

  const handleCopy = async () => {
    if (!createdKey) return;
    try {
      await navigator.clipboard.writeText(createdKey);
      setCopied(true);
    } catch {
      // Clipboard can be unavailable (e.g. non-HTTPS); the key is still visible to copy manually.
    }
  };

  const formatDate = (value: string | null) => {
    if (!value) return "Never";
    const date = new Date(value);
    return isNaN(date.getTime()) ? value : date.toLocaleString();
  };

  if (isLoading || loadingKeys) {
    return (
      <PageLayout>
        <div className="content-layout">
          <div className="content__body">
            <p className="text-center text-[#9aa5b8]">Loading...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="content-layout">
        <div className="content__body p-4">
          <div className="max-w-3xl mx-auto">
            <div className="mb-8">
              <h3 className="text-2xl font-bold mb-4 text-[#e8ecf4]">API Keys</h3>
              <p className="text-l text-[#9aa5b8]">
                API keys allow other programs and websites to use and interact
                with your Strongifier data, such as fetching your workout
                history. Treat each key like a password: anyone who has it can
                read your data. You can have up to 25 keys at a time. See the{" "}
                <Link to="/developer" className="underline">
                  developer documentation
                </Link>{" "}
                for how to use these keys.
              </p>
            </div>

            {error && (
              <div className="mb-4 p-4 bg-red-500/10 border border-red-500/40 text-red-400 rounded-[10px]">
                {error}
              </div>
            )}

            {/* Create a new key */}
            <div className="mb-8">
              <h4 className="text-xl font-bold mb-4 text-[#e8ecf4]">
                Create a New API Key
              </h4>
              <div className="bg-[#1a2130] border border-[#242e42] rounded-[10px] p-6">
                <div className="flex gap-4">
                  <input
                    type="text"
                    value={newKeyName}
                    onChange={(e) => setNewKeyName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                    placeholder="Key name (e.g. My Training Dashboard)"
                    maxLength={100}
                    className="flex-1 bg-[#10151f] border border-[#2e3850] rounded px-3 py-2 text-[#e8ecf4] placeholder-[#6b7688]"
                  />
                  <button
                    onClick={handleCreate}
                    disabled={creating || !newKeyName.trim()}
                    className="bg-[#5661f0] hover:bg-[#4650e0] text-white px-4 py-2 rounded-[8px] disabled:opacity-50"
                  >
                    {creating ? "Creating..." : "Create Key"}
                  </button>
                </div>

                {createdKey && (
                  <div className="mt-4 p-4 bg-[#f0862f]/10 border border-[#f0862f]/40 rounded">
                    <p className="text-[#e8ecf4] font-medium mb-2">
                      Your new key "{createdKeyName}" has been created. Copy it
                      now — for your security, it will not be shown again.
                    </p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 bg-[#10151f] text-[#e8ecf4] p-2 rounded break-all text-sm">
                        {createdKey}
                      </code>
                      <button
                        onClick={handleCopy}
                        className="bg-transparent border border-[#2e3850] hover:border-[#46527a] text-[#aab6d0] px-3 py-2 rounded-[8px] text-sm"
                      >
                        {copied ? "Copied!" : "Copy"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Existing keys */}
            <div className="mb-8">
              <h4 className="text-xl font-bold mb-4 text-[#e8ecf4]">Your Keys</h4>
              <div className="bg-[#1a2130] border border-[#242e42] rounded-[10px] p-6">
                {apiKeys.length === 0 ? (
                  <p className="text-[#9aa5b8]">
                    You don't have any API keys yet.
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left">
                      <thead>
                        <tr className="text-[#9aa5b8] border-b border-[#232c3e]">
                          <th className="py-2 pr-4">Name</th>
                          <th className="py-2 pr-4">Key</th>
                          <th className="py-2 pr-4">Created</th>
                          <th className="py-2 pr-4">Last Used</th>
                          <th className="py-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {apiKeys.map((key) => (
                          <tr key={key.id} className="border-b border-[#232c3e] text-[#e8ecf4]">
                            <td className="py-2 pr-4">{key.name}</td>
                            <td className="py-2 pr-4">
                              <code className="text-sm">
                                {key.key_prefix}...
                              </code>
                            </td>
                            <td className="py-2 pr-4">
                              {formatDate(key.created_at)}
                            </td>
                            <td className="py-2 pr-4">
                              {formatDate(key.last_used_at)}
                            </td>
                            <td className="py-2 text-right">
                              <button
                                onClick={() => handleDelete(key)}
                                disabled={deletingId === key.id}
                                className="text-red-400 hover:text-red-300 underline disabled:opacity-50"
                              >
                                {deletingId === key.id
                                  ? "Deleting..."
                                  : "Delete"}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};
